import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import ParticleBackground from "@/components/ParticleBackground";
import AIGrid from "@/components/AIGrid";
import SEO from "@/components/SEO";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search, MessageCircle } from "lucide-react";
import { motion } from "framer-motion";
import { useState } from "react";
import { faqSchema } from "@/utils/structuredData";

const FAQ = () => {
  const [searchQuery, setSearchQuery] = useState("");

  const faqData = {
    product: [
      {
        question: "What is Holionix AI Voice Platform?",
        answer: "Holionix is an enterprise-grade AI voice platform that automates customer interactions through intelligent voice agents. Our solution handles inbound support, outbound sales, and customer service calls with natural, human-like conversations while maintaining GDPR compliance and ISO 27001 certification."
      },
      {
        question: "How does Holionix differ from traditional IVR systems?",
        answer: "Unlike traditional IVR systems with rigid menu trees, Holionix uses advanced natural language processing and generative AI to understand context, handle complex queries, and provide personalized responses. Our agents learn from interactions and can handle nuanced conversations that would typically require human intervention."
      },
      {
        question: "What languages does Holionix support?",
        answer: "Holionix supports 40+ languages including English, German, French, Spanish, Italian, Dutch, Polish, and more. Our multilingual capabilities enable seamless international operations with consistent quality across all languages."
      },
      {
        question: "Can Holionix integrate with our existing systems?",
        answer: "Yes, Holionix offers comprehensive integration capabilities with major CRM platforms (Salesforce, HubSpot, Microsoft Dynamics), helpdesk systems (Zendesk, Freshdesk), telephony providers, and custom APIs. Our integration hub supports both standard connectors and custom integrations."
      }
    ],
    technical: [
      {
        question: "What infrastructure does Holionix run on?",
        answer: "Holionix runs on enterprise-grade cloud infrastructure with multi-region deployment across AWS and Azure. We maintain 99.9% uptime SLA with automatic failover, load balancing, and redundant systems to ensure reliability for mission-critical operations."
      },
      {
        question: "How do you handle data security and privacy?",
        answer: "We implement bank-level encryption (AES-256 at rest, TLS 1.3 in transit), maintain ISO 27001 and SOC 2 Type II certifications, and are fully GDPR compliant. All data is stored in EU data centers with strict access controls and regular security audits."
      },
      {
        question: "What is the typical implementation timeline?",
        answer: "Standard implementation takes 2-4 weeks including setup, integration, training, and testing. Enterprise deployments with complex integrations may take 6-8 weeks. We provide dedicated implementation support and project management throughout the process."
      },
      {
        question: "How scalable is the platform?",
        answer: "Holionix scales automatically to handle from 10 to 10,000+ concurrent calls. Our cloud-native architecture supports elastic scaling, ensuring consistent performance during peak periods without manual intervention or capacity planning."
      }
    ],
    pricing: [
      {
        question: "How is Holionix priced?",
        answer: "We offer flexible pricing based on call volume, features, and deployment requirements. Pricing includes per-minute usage fees plus platform subscription. Contact our sales team for a custom quote based on your specific needs and volume projections."
      },
      {
        question: "Is there a minimum contract period?",
        answer: "Our standard contracts are 12-month commitments with monthly billing. We also offer annual prepaid options with volume discounts. Enterprise customers can discuss custom contract terms with our sales team."
      },
      {
        question: "What's included in the platform fee?",
        answer: "The platform fee includes access to all core features, standard integrations, 24/7 technical support, regular updates, security patches, basic training, and usage analytics. Premium features like advanced analytics and dedicated account management are available as add-ons."
      },
      {
        question: "Do you offer a free trial?",
        answer: "Yes, we offer a 14-day pilot program for qualified enterprises. This includes full platform access, implementation support, and up to 500 test calls to evaluate performance with your actual use cases before committing to a contract."
      }
    ],
    compliance: [
      {
        question: "Is Holionix GDPR compliant?",
        answer: "Yes, Holionix is fully GDPR compliant. We maintain data processing agreements (DPAs), implement privacy by design, support data subject rights (access, deletion, portability), and store all EU customer data within EU data centers with strict data residency controls."
      },
      {
        question: "What compliance certifications do you have?",
        answer: "We maintain ISO 27001 (Information Security), SOC 2 Type II (Security & Availability), GDPR compliance, and are working towards ISO 9001 certification. We undergo annual third-party audits and continuous compliance monitoring."
      },
      {
        question: "How do you handle call recording and consent?",
        answer: "We provide configurable consent management with automated disclosure messages, opt-in/opt-out capabilities, and detailed audit trails. All recordings are encrypted, access-controlled, and retained according to your compliance requirements and regional regulations."
      },
      {
        question: "Can we conduct our own security audits?",
        answer: "Yes, enterprise customers can conduct security assessments and penetration testing in coordination with our security team. We provide detailed security documentation, architecture diagrams, and work collaboratively to address any findings."
      }
    ]
  };

  const allFaqs = [
    ...faqData.product,
    ...faqData.technical,
    ...faqData.pricing,
    ...faqData.compliance
  ];

  const filteredSections = searchQuery
    ? {
        product: faqData.product.filter(faq => 
          faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
          faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
        ),
        technical: faqData.technical.filter(faq =>
          faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
          faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
        ),
        pricing: faqData.pricing.filter(faq =>
          faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
          faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
        ),
        compliance: faqData.compliance.filter(faq =>
          faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
          faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
        )
      }
    : faqData;

  const sections = [
    { key: 'product', title: 'Product & Features', icon: '🚀', data: filteredSections.product },
    { key: 'technical', title: 'Technical & Implementation', icon: '⚙️', data: filteredSections.technical },
    { key: 'pricing', title: 'Pricing & Plans', icon: '💰', data: filteredSections.pricing },
    { key: 'compliance', title: 'Security & Compliance', icon: '🔒', data: filteredSections.compliance }
  ];

  return (
    <div className="min-h-screen flex flex-col bg-background relative overflow-hidden">
      <SEO
        title="FAQ - Holionix AI Voice Agents | Frequently Asked Questions"
        description="Find answers to frequently asked questions about Holionix AI voice platform, pricing, security, compliance, and implementation."
        structuredData={faqSchema(allFaqs)}
      />
      <ParticleBackground />
      <AIGrid />
      <Navigation />
      
      <main className="flex-grow pt-32 pb-20 relative z-10">
        <div className="container mx-auto px-4 max-w-5xl">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <h1 className="text-4xl md:text-6xl font-bold mb-6 text-shadow-glow">
              Frequently Asked <span className="gradient-brand-text">Questions</span>
            </h1>
            <p className="text-xl text-foreground/80 max-w-3xl mx-auto mb-8">
              Everything you need to know about Holionix AI Voice Platform
            </p>

            <div className="relative max-w-2xl mx-auto">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-foreground/50 w-5 h-5" />
              <Input
                type="text"
                placeholder="Search for answers..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 h-12 text-base"
              />
            </div>
          </motion.div>

          {sections.map((section, sectionIndex) => (
            section.data.length > 0 && (
              <motion.div
                key={section.key}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: sectionIndex * 0.1 }}
                className="mb-12"
              >
                <h2 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-3">
                  <span>{section.icon}</span>
                  {section.title}
                </h2>
                <Card className="card-dark gradient-border">
                  <Accordion type="single" collapsible className="w-full">
                    {section.data.map((faq, index) => (
                      <AccordionItem key={index} value={`${section.key}-${index}`} className="border-border/50">
                        <AccordionTrigger className="text-left px-6 py-4 hover:text-accent transition-colors">
                          <span className="font-semibold">{faq.question}</span>
                        </AccordionTrigger>
                        <AccordionContent className="px-6 pb-4 text-foreground/80 leading-relaxed">
                          {faq.answer}
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </Card>
              </motion.div>
            )
          ))}

          {searchQuery && 
           filteredSections.product.length === 0 && 
           filteredSections.technical.length === 0 &&
           filteredSections.pricing.length === 0 &&
           filteredSections.compliance.length === 0 && (
            <Card className="card-dark p-12 text-center">
              <p className="text-foreground/70 text-lg mb-4">
                No results found for "{searchQuery}"
              </p>
              <Button 
                variant="outline" 
                onClick={() => setSearchQuery("")}
                className="border-accent text-accent hover:bg-accent/10"
              >
                Clear Search
              </Button>
            </Card>
          )}

          <Card className="mt-16 p-12 card-dark gradient-border text-center glow-effect">
            <MessageCircle className="w-16 h-16 text-accent mx-auto mb-6" />
            <h2 className="text-3xl font-bold mb-4">Still have questions?</h2>
            <p className="text-foreground/70 mb-6 max-w-2xl mx-auto">
              Can't find the answer you're looking for? Our team is here to help you with any questions about Holionix.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button className="gradient-brand text-white hover:opacity-90">
                Contact Sales
              </Button>
              <Button variant="outline" className="border-accent text-accent hover:bg-accent/10">
                Schedule a Demo
              </Button>
            </div>
          </Card>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default FAQ;
